(() => {
  let activeRequestController = null;

  const getFormUrl = (form, submitter = null) => {
    const url = new URL(form.action || window.location.href, window.location.origin);
    const formData = new FormData(form);

    if (submitter?.name) formData.set(submitter.name, submitter.value);

    url.search = '';
    formData.forEach((value, name) => {
      if (typeof value === 'string' && value !== '') url.searchParams.append(name, value);
    });

    return url;
  };

  const getFocusedControl = (form) => {
    const control = form.querySelector(':focus');
    if (!control?.name) return null;

    return {
      name: control.name,
      value: control.value,
      type: control.type
    };
  };

  const restoreFocusedControl = (root, controlState) => {
    if (!controlState) return;

    const match = Array.from(root.querySelectorAll('[name]')).find((control) => {
      if (control.name !== controlState.name || control.type !== controlState.type) return false;
      return control.type === 'checkbox' ? control.value === controlState.value : true;
    });

    match?.focus({ preventScroll: true });
  };

  const announceResults = (root) => {
    const status = root.querySelector('[data-collection-results-status]');
    const count = Number.parseInt(root.dataset.collectionResultCount || '', 10);
    if (!status || !Number.isFinite(count)) return;

    status.textContent = `${count} produit${count === 1 ? '' : 's'} affiche${count === 1 ? '' : 's'}.`;
  };

  const renderCollection = async (url, options = {}) => {
    const currentRoot = document.querySelector('[data-collection-page]');
    const sectionId = currentRoot?.dataset.sectionId;
    if (!currentRoot || !sectionId) {
      window.location.assign(url.href);
      return;
    }

    activeRequestController?.abort();
    const requestController = new AbortController();
    activeRequestController = requestController;
    currentRoot.setAttribute('aria-busy', 'true');

    const requestUrl = new URL(url.href);
    requestUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: requestController.signal
      });

      if (!response.ok) throw new Error(`Collection request failed with status ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = documentFragment.querySelector('[data-collection-page]');
      if (!nextRoot) throw new Error('Collection section was missing from the response');

      if (options.openDisclosureType) {
        const nextDisclosure = nextRoot.querySelector(
          `[data-collection-disclosure-type="${options.openDisclosureType}"]`
        );
        if (nextDisclosure) nextDisclosure.open = true;
      }

      currentRoot._collectionAbortController?.abort();
      currentRoot.replaceWith(nextRoot);

      if (options.updateHistory !== false) {
        window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      initCollectionPage(nextRoot);
      restoreFocusedControl(nextRoot, options.focusedControl);
      announceResults(nextRoot);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url.href);
    } finally {
      if (activeRequestController === requestController) {
        activeRequestController = null;
        currentRoot.removeAttribute('aria-busy');
      }
    }
  };

  const initCollectionPage = (root) => {
    if (!root || root.dataset.collectionInitialized === 'true') return;

    const disclosures = Array.from(root.querySelectorAll('[data-collection-disclosure]'));
    if (!disclosures.length) return;

    root.dataset.collectionInitialized = 'true';
    const eventController = new AbortController();
    const { signal } = eventController;
    root._collectionAbortController = eventController;

    const setSummaryState = (details) => {
      const summary = details.querySelector('summary[aria-expanded]');
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    };

    const closeDisclosure = (details) => {
      details.open = false;
      setSummaryState(details);
    };

    disclosures.forEach((details) => {
      setSummaryState(details);

      details.addEventListener('toggle', () => {
        setSummaryState(details);

        if (!details.open) return;

        disclosures.forEach((otherDetails) => {
          if (otherDetails !== details) closeDisclosure(otherDetails);
        });
      }, { signal });

      details.querySelectorAll('[data-collection-close]').forEach((button) => {
        button.addEventListener('click', () => {
          closeDisclosure(details);
          details.querySelector('summary')?.focus();
        }, { signal });
      });
    });

    root.querySelectorAll('.collection-filter-form').forEach((form) => {
      let submitTimer = null;
      let submitFrame = null;

      const clearScheduledSubmit = () => {
        window.clearTimeout(submitTimer);
        submitTimer = null;

        if (submitFrame !== null) {
          window.cancelAnimationFrame(submitFrame);
          submitFrame = null;
        }
      };

      const submitFilters = () => {
        clearScheduledSubmit();

        submitFrame = window.requestAnimationFrame(() => {
          submitFrame = null;

          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
          }

          form.submit();
        });
      };

      const scheduleFilterSubmit = (delay = 350) => {
        clearScheduledSubmit();
        submitTimer = window.setTimeout(submitFilters, delay);
      };

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearScheduledSubmit();
        renderCollection(getFormUrl(form, event.submitter), {
          openDisclosureType: 'filters',
          focusedControl: getFocusedControl(form)
        });
      }, { signal });

      form.querySelectorAll('.collection-filter-form__actions a').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          clearScheduledSubmit();
          renderCollection(new URL(link.href), { openDisclosureType: 'filters' });
        }, { signal });
      });

      const parsePriceValue = (value) => {
        const normalizedValue = String(value || '').replace(',', '.');
        const parsedValue = Number.parseFloat(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
      };

      const clampPriceValue = (value, min, max) => Math.min(Math.max(value, min), max);

      const roundPriceValue = (value) => Math.round(value * 100) / 100;

      const getSliderPosition = (value, min, max) => {
        const range = max - min;
        if (range <= 0) return '7px';

        const ratio = clampPriceValue((value - min) / range, 0, 1);
        const percentage = ratio * 100;
        const thumbOffset = 7 - ratio * 14;

        return `calc(${percentage}% + ${thumbOffset}px)`;
      };

      const updatePriceSliderPosition = (rangeRoot, minValue, maxValue, rangeMin, rangeMax) => {
        const slider = rangeRoot.querySelector('[data-price-slider]');
        if (!slider) return;

        rangeRoot.style.setProperty(
          '--price-min-position',
          getSliderPosition(minValue, rangeMin, rangeMax)
        );
        rangeRoot.style.setProperty(
          '--price-max-position',
          getSliderPosition(maxValue, rangeMin, rangeMax)
        );
      };

      const syncPriceRange = (rangeRoot, source) => {
        const minRange = rangeRoot.querySelector('[data-price-min-range]');
        const maxRange = rangeRoot.querySelector('[data-price-max-range]');
        const minInput = rangeRoot.querySelector('[data-price-min-input]');
        const maxInput = rangeRoot.querySelector('[data-price-max-input]');
        if (!minRange || !maxRange || !minInput || !maxInput) return;

        const rangeMin = parsePriceValue(minRange.min) ?? 0;
        const rangeMax = parsePriceValue(minRange.max) ?? 0;
        let minValue = parsePriceValue(minRange.value) ?? rangeMin;
        let maxValue = parsePriceValue(maxRange.value) ?? rangeMax;

        if (source === minInput) {
          minValue = parsePriceValue(minInput.value) ?? rangeMin;
        } else if (source === maxInput) {
          maxValue = parsePriceValue(maxInput.value) ?? rangeMax;
        } else if (source === minRange) {
          minValue = parsePriceValue(minRange.value) ?? rangeMin;
        } else if (source === maxRange) {
          maxValue = parsePriceValue(maxRange.value) ?? rangeMax;
        }

        minValue = roundPriceValue(clampPriceValue(minValue, rangeMin, rangeMax));
        maxValue = roundPriceValue(clampPriceValue(maxValue, rangeMin, rangeMax));

        if (minValue > maxValue) {
          if (source === minInput || source === minRange) {
            maxValue = minValue;
          } else {
            minValue = maxValue;
          }
        }

        minRange.value = String(minValue);
        maxRange.value = String(maxValue);
        updatePriceSliderPosition(rangeRoot, minValue, maxValue, rangeMin, rangeMax);

        if (source === minRange || source === minInput) minInput.value = minRange.value;
        if (source === maxRange || source === maxInput) maxInput.value = maxRange.value;
      };

      const getPointerPriceValue = (slider, event, rangeMin, rangeMax) => {
        const rect = slider.getBoundingClientRect();
        const usableWidth = Math.max(rect.width - 14, 1);
        const x = clampPriceValue(event.clientX - rect.left - 7, 0, usableWidth);
        const ratio = x / usableWidth;

        return roundPriceValue(rangeMin + ratio * (rangeMax - rangeMin));
      };

      form.querySelectorAll('.collection-price-range').forEach((rangeRoot) => {
        const minRange = rangeRoot.querySelector('[data-price-min-range]');
        const maxRange = rangeRoot.querySelector('[data-price-max-range]');
        const slider = rangeRoot.querySelector('[data-price-slider]');
        const numberInputs = rangeRoot.querySelectorAll('input[type="number"]');
        let activeRange = null;
        let activePointerId = null;

        minRange?.addEventListener('input', () => {
          syncPriceRange(rangeRoot, minRange);
          scheduleFilterSubmit(300);
        }, { signal });
        maxRange?.addEventListener('input', () => {
          syncPriceRange(rangeRoot, maxRange);
          scheduleFilterSubmit(300);
        }, { signal });
        minRange?.addEventListener('change', submitFilters, { signal });
        maxRange?.addEventListener('change', submitFilters, { signal });

        const moveActiveRange = (event) => {
          if (!activeRange || !slider || !minRange || !maxRange) return;
          if (activePointerId !== null && event.pointerId !== activePointerId) return;

          const rangeMin = parsePriceValue(minRange.min) ?? 0;
          const rangeMax = parsePriceValue(minRange.max) ?? 0;
          let nextValue = getPointerPriceValue(slider, event, rangeMin, rangeMax);
          const currentMin = parsePriceValue(minRange.value) ?? rangeMin;
          const currentMax = parsePriceValue(maxRange.value) ?? rangeMax;

          if (activeRange === minRange) {
            nextValue = Math.min(nextValue, currentMax);
          } else {
            nextValue = Math.max(nextValue, currentMin);
          }

          activeRange.value = String(nextValue);
          syncPriceRange(rangeRoot, activeRange);
        };

        const stopActiveRange = (event) => {
          if (!activeRange) return;
          if (activePointerId !== null && event.pointerId !== activePointerId) return;

          activeRange = null;
          activePointerId = null;
          window.removeEventListener('pointermove', moveActiveRange);
          window.removeEventListener('pointerup', stopActiveRange);
          window.removeEventListener('pointercancel', stopActiveRange);
          submitFilters();
        };

        slider?.addEventListener('pointerdown', (event) => {
          if (!minRange || !maxRange) return;

          event.preventDefault();

          const rangeMin = parsePriceValue(minRange.min) ?? 0;
          const rangeMax = parsePriceValue(minRange.max) ?? 0;
          const pointerValue = getPointerPriceValue(slider, event, rangeMin, rangeMax);
          const minValue = parsePriceValue(minRange.value) ?? rangeMin;
          const maxValue = parsePriceValue(maxRange.value) ?? rangeMax;

          activeRange = Math.abs(pointerValue - minValue) <= Math.abs(pointerValue - maxValue) ? minRange : maxRange;
          activePointerId = event.pointerId;

          moveActiveRange(event);
          window.addEventListener('pointermove', moveActiveRange, { signal });
          window.addEventListener('pointerup', stopActiveRange, { signal, once: true });
          window.addEventListener('pointercancel', stopActiveRange, { signal, once: true });
        }, { signal });

        numberInputs.forEach((input) => {
          input.addEventListener('input', () => {
            syncPriceRange(rangeRoot, input);
            scheduleFilterSubmit(500);
          }, { signal });

          input.addEventListener('change', () => {
            syncPriceRange(rangeRoot, input);
            submitFilters();
          }, { signal });

          input.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            syncPriceRange(rangeRoot, input);
            submitFilters();
          }, { signal });
        });

        syncPriceRange(rangeRoot);
      });

      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', submitFilters, { signal });
      });
    });

    root.querySelectorAll('.collection-sort-form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        renderCollection(getFormUrl(form, event.submitter));
      }, { signal });
    });

    document.addEventListener('click', (event) => {
      if (root.contains(event.target)) {
        disclosures.forEach((details) => {
          if (details.open && !details.contains(event.target)) closeDisclosure(details);
        });

        return;
      }

      disclosures.forEach(closeDisclosure);
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const openDisclosure = disclosures.find((details) => details.open);
      if (!openDisclosure) return;

      closeDisclosure(openDisclosure);
      openDisclosure.querySelector('summary')?.focus();
    }, { signal });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-collection-page]')) {
      initCollectionPage(scope);
      return;
    }

    scope.querySelectorAll?.('[data-collection-page]').forEach(initCollectionPage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-collection-page]')?._collectionAbortController?.abort();
  });

  window.addEventListener('popstate', () => {
    const root = document.querySelector('[data-collection-page]');
    if (!root) return;

    const openDisclosureType = root.querySelector('[data-collection-disclosure][open]')?.dataset
      .collectionDisclosureType;

    renderCollection(new URL(window.location.href), {
      updateHistory: false,
      openDisclosureType
    });
  });
})();
